import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Flame, ArrowRight, TrendUp, Sparkle, Kanban, Plus, EnvelopeOpen, Spinner, UsersThree, ChartLineUp } from "@phosphor-icons/react";
import api, { formatApiErrorDetail } from "@/lib/api";
import AppShell from "@/components/AppShell";
import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { ScoreBadge } from "@/components/ScoreBadge";
import UsageStrip from "@/components/UsageStrip";
import ReferralLeaderboard from "@/components/ReferralLeaderboard";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

function MetricCard({ label, value, accent, testid, icon: Icon }) {
  return (
    <div
      data-testid={testid}
      className="bg-zinc-900/60 border border-zinc-800 rounded-sm p-5 relative overflow-hidden"
    >
      <div className="flex items-center justify-between mb-3">
        <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-zinc-500">{label}</div>
        {Icon ? <Icon size={16} weight="duotone" className={accent || "text-zinc-500"} /> : null}
      </div>
      <div className={`font-display text-3xl font-semibold ${accent || "text-zinc-50"}`}>{value}</div>
    </div>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [leads, setLeads] = useState([]);
  const [digestPreview, setDigestPreview] = useState(null);
  const [sendingDigest, setSendingDigest] = useState(false);
  const [adminStats, setAdminStats] = useState(null);
  const navigate = useNavigate();

  const load = async () => {
    const [s, l, dp] = await Promise.all([
      api.get("/stats"),
      api.get("/leads"),
      api.get("/email/digest/preview").catch(() => ({ data: null })),
    ]);
    setStats(s.data);
    setLeads(l.data);
    setDigestPreview(dp.data);
  };

  useEffect(() => { load(); }, []);

  useEffect(() => {
    if (user?.role !== "admin") return;
    api.get("/admin/stats").then((r) => setAdminStats(r.data)).catch(() => {});
  }, [user]);

  const sendDigestNow = async () => {
    setSendingDigest(true);
    try {
      const { data } = await api.post("/email/digest/send");
      if (data.sent) {
        toast.success("Digest sent to your inbox.");
      } else {
        toast.message(`Digest skipped: ${data.reason}`);
      }
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || err.message);
    } finally {
      setSendingDigest(false);
    }
  };

  const topHot = useMemo(
    () => [...leads].sort((a, b) => (b.lead_score || 0) - (a.lead_score || 0)).slice(0, 5),
    [leads],
  );

  return (
    <AppShell>
      <PageHeader
        eyebrow="Control Room"
        title="Dashboard"
        subtitle="Your live pipeline overview — discover, qualify, and ignite outreach."
        testid="dashboard-header"
      >
        <Button
          data-testid="dashboard-discover-cta"
          onClick={() => navigate("/discover")}
          className="bg-orange-600 hover:bg-orange-500 text-zinc-950 rounded-sm"
        >
          <Sparkle size={16} className="mr-2" weight="fill" /> Find Real Businesses
        </Button>
      </PageHeader>

      <div className="px-4 md:px-10 py-8 space-y-8">
        {digestPreview && Object.values(digestPreview.counts || {}).some((v) => v > 0) ? (
          <div
            data-testid="dashboard-digest-banner"
            className="bg-gradient-to-r from-orange-600/10 via-zinc-900/40 to-zinc-900/40 border border-orange-600/30 rounded-sm p-4 flex flex-wrap items-center justify-between gap-3"
          >
            <div className="flex items-center gap-3">
              <EnvelopeOpen size={20} weight="duotone" className="text-orange-500" />
              <div>
                <div className="font-display text-sm font-medium text-zinc-100">Your morning digest is ready</div>
                <div className="text-xs text-zinc-400 mt-0.5 font-mono">
                  <span data-testid="digest-count-new">{digestPreview.counts.new_leads}</span> new ·{" "}
                  <span data-testid="digest-count-regressions">{digestPreview.counts.regressions}</span> regressed ·{" "}
                  <span data-testid="digest-count-followup">{digestPreview.counts.opened_unreplied}</span> need follow-up ·{" "}
                  <span data-testid="digest-count-stale">{digestPreview.counts.stale_proposals}</span> stale proposals
                </div>
              </div>
            </div>
            <Button
              data-testid="dashboard-send-digest"
              onClick={sendDigestNow}
              disabled={sendingDigest}
              variant="outline"
              size="sm"
              className="border-orange-600/40 hover:bg-orange-600/10 text-orange-300 rounded-sm"
            >
              {sendingDigest ? <Spinner size={14} className="animate-spin mr-1.5" /> : <EnvelopeOpen size={14} weight="fill" className="mr-1.5" />}
              Email to me now
            </Button>
          </div>
        ) : null}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <MetricCard label="Total Leads" value={stats?.total ?? "—"} testid="metric-total" icon={Kanban} />
          <MetricCard label="Hot (70+)" value={stats?.hot ?? "—"} accent="text-orange-400" testid="metric-hot" icon={Flame} />
          <MetricCard label="Warm (40–69)" value={stats?.warm ?? "—"} accent="text-amber-300" testid="metric-warm" icon={TrendUp} />
          <MetricCard label="Cold (<40)" value={stats?.cold ?? "—"} accent="text-zinc-300" testid="metric-cold" />
        </div>

        {/* Free-tier usage meters (auto-hides for Pro/Agency/Trialing) */}
        <UsageStrip />

        {/* Admin-only: ClickForge platform signups */}
        {user?.role === "admin" && adminStats && (
          <div data-testid="admin-stats-section" className="space-y-3">
            <div className="flex items-center gap-2">
              <ChartLineUp size={16} weight="duotone" className="text-orange-500" />
              <div className="font-display font-medium text-zinc-100 text-sm">ClickForge platform · admin only</div>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <MetricCard label="Total Users" value={adminStats.total_users} testid="admin-metric-total-users" icon={UsersThree} />
              <MetricCard label="Signups Today" value={adminStats.signups_today} accent="text-orange-400" testid="admin-metric-today" />
              <MetricCard label="Signups This Week" value={adminStats.signups_this_week} accent="text-amber-300" testid="admin-metric-week" />
              <MetricCard label="Signups This Month" value={adminStats.signups_this_month} accent="text-emerald-300" testid="admin-metric-month" />
            </div>
            {adminStats.recent_signups?.length > 0 && (
              <div className="bg-zinc-900/40 border border-zinc-800 rounded-sm overflow-hidden">
                <div className="px-4 py-2 border-b border-zinc-800 text-[10px] font-mono uppercase tracking-[0.2em] text-zinc-500">
                  Recent signups
                </div>
                <div className="divide-y divide-zinc-800/60">
                  {adminStats.recent_signups.slice(0, 5).map((u) => (
                    <div
                      key={u.email}
                      data-testid={`recent-signup-${u.email}`}
                      className="px-4 py-2 flex items-center justify-between gap-3 hover:bg-zinc-800/30"
                    >
                      <div className="min-w-0 flex-1">
                        <div className="text-sm text-zinc-100 truncate">{u.name}</div>
                        <div className="text-[11px] font-mono text-zinc-500 truncate">{u.email}</div>
                      </div>
                      <div className="text-[11px] font-mono text-zinc-500">
                        {u.created_at ? new Date(u.created_at).toLocaleDateString() : "—"}
                      </div>
                      {u.role === "admin" && (
                        <span className="text-[9px] font-mono uppercase tracking-wider text-orange-400 bg-orange-600/10 border border-orange-600/30 rounded-sm px-1.5 py-0.5">
                          admin
                        </span>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Referral leaderboard — admin only */}
        {user?.role === "admin" && <ReferralLeaderboard />}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <div className="lg:col-span-2 bg-zinc-900/60 border border-zinc-800 rounded-sm">
            <div className="border-b border-zinc-800 px-4 py-3 flex items-center justify-between">
              <div className="font-display font-medium text-zinc-100">Top Hot Leads</div>
              <Button
                variant="ghost"
                size="sm"
                data-testid="dashboard-view-pipeline"
                onClick={() => navigate("/pipeline")}
                className="text-zinc-400 hover:text-zinc-100 text-xs"
              >
                View pipeline <ArrowRight size={14} className="ml-1" />
              </Button>
            </div>
            <div className="divide-y divide-zinc-800/60">
              {topHot.length === 0 && (
                <div className="px-4 py-10 text-center" data-testid="empty-top-leads">
                  <div className="text-sm text-zinc-500 mb-3">No leads yet. Find real Midwest businesses via OpenStreetMap.</div>
                  <Button
                    data-testid="empty-discover-btn"
                    onClick={() => navigate("/discover")}
                    className="bg-orange-600 hover:bg-orange-500 text-zinc-950 rounded-sm"
                  >
                    <Plus size={16} className="mr-2" /> Run Discovery
                  </Button>
                </div>
              )}
              {topHot.map((l) => (
                <div
                  key={l.id}
                  data-testid={`top-lead-${l.id}`}
                  className="px-4 py-3 hover:bg-zinc-800/30 transition-colors flex items-center justify-between gap-3 cursor-pointer"
                  onClick={() => navigate(`/pipeline?lead=${l.id}`)}
                >
                  <div className="min-w-0">
                    <div className="text-sm text-zinc-100 truncate">{l.business_name}</div>
                    <div className="text-xs text-zinc-500 font-mono truncate">
                      {l.industry} · {l.city}{l.state ? `, ${l.state}` : ""}
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="stage-pill text-zinc-400">{l.status}</span>
                    <ScoreBadge score={l.lead_score} />
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="bg-zinc-900/60 border border-zinc-800 rounded-sm">
            <div className="border-b border-zinc-800 px-4 py-3 font-display font-medium text-zinc-100">
              Pipeline Stages
            </div>
            <div className="p-2 max-h-[420px] overflow-y-auto">
              {stats?.stages?.map((s) => {
                const count = stats?.by_stage?.[s] || 0;
                return (
                  <div
                    key={s}
                    data-testid={`stage-row-${s}`}
                    className="flex items-center justify-between px-3 py-2 hover:bg-zinc-800/40 rounded-sm cursor-pointer"
                    onClick={() => navigate("/pipeline")}
                  >
                    <span className="text-sm text-zinc-300">{s}</span>
                    <span className="font-mono text-xs text-orange-400">{count}</span>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </AppShell>
  );
}
